/* 曹純（字子和） */
import { SG, K, gd, spMul, sigOf, landing, along } from '../../core/kit.ts';
import type { Unit } from '../types.ts';
import { defineHero, hook, U, D, S } from '../define.ts';

export default defineHero({
  id: 'caochun', name: '曹純', sub: '字子和', faction: 'wei', role: '射手', weapon: 'bow',
  look: { weaponName: '虎豹騎弓', beard: 'goatee', head: 'helmet', fx: '#e0b050', c: { armor: '#8a6a2a', sleeve: '#6a4e1e', plume: '#e07020', cape: '#3a2a14' } },
  skills: [
    ['回馬箭', 'bolt', '朝游標反方向後躍 250，同時朝游標射一箭 {70/110/150/190}；命中武將時 4 秒內普攻射程 +100。', { x: { k: 'parthian', dmg: [70, 110, 150, 190], back: 250 }, aim: D(760, 20), range: 760, cd: [12, 11, 10, 9], mp: [60, 65, 70, 75], el: 'wind' }],
    ['精騎疾馳', 'passive', '每移動 400 距離，下一次普攻附帶衝擊，額外 {30/50/70/90} 傷害並擊退。', { pv: {}, sig: 'gallop', dmg: [30, 50, 70, 90], ready: (u) => (u.gallopRun || 0) >= 400 }],
    ['長坂追擊', 'command', '標記一名敵將 8 秒：朝他移動時移速 +{10/13/15/18}%；他背對曹純移動（逃跑）時受到曹純的傷害 +15%。', { x: { k: 'pursue', ms: [10, 13, 15, 18], dur: 8 }, aim: U(900), cd: [20, 18, 16, 14], mp: 70, el: null }],
    ['虎豹騎突陣', 'summon', '召喚 {4/5/6} 名虎豹騎跟隨 8 秒，衝向曹純攻擊的目標，每擊 {40/55/70}，第一次命中擊退；曹純移速 +10%。', { x: { k: 'hubao', n: [4, 5, 6], dmg: [40, 55, 70], dur: 8 }, aim: S(0), cd: [90, 80, 70], mp: [130, 150, 170], el: null, shout: '虎豹騎，隨我追！' }],
  ],
  // 專屬技能（技能資料裡的 x.k）
  kinds: {
    parthian(u, a, X) {
      const dir = a.dir, sp = spMul(u), p = landing(u.x, u.y, ...along(u.x, u.y, [-dir[0], -dir[1]], X.back) as [number, number]);
      this.fx({ k: 'puff', x: u.x, y: u.y }); u.x = p[0]; u.y = p[1];
      this.shootProj(u, { dir, speed: 1150, range: this.skillRange(a.sk, a.lvl), shape: 'arrow', col: u.spec.fx,
        filter: (t: Unit) => !t.structure,
        onHit: (t: Unit) => {
          this.damage(u, t, X.dmg * sp, { proj: true, stagger: 0.2 });
          if (t.kind === 'hero') { u.parthianT = this.t + 4; this.setRangeBonus(u, 'parthian', 100); }
          return true;
        } });
    },
    pursue(u, a, X) {
      const t = this.xPick(u, a.pt, 160);
      if (!t || t.kind !== 'hero') { this.tell(u, '長坂追擊：要指定敵將', 'warn'); return; }
      u.pursue = { t, until: this.t + X.dur, ms: X.ms };
      this.expose(t, u.team, X.dur);
      this.fx({ k: 'warn', x: t.x, y: t.y, r: 50, t: 1 });
    },
    hubao(u, a, X) {
      this.units.forEach((o) => { if (o.alive && o.hubao && o.owner === u) { o.alive = false; o.deadT = this.t; } });
      const sp = spMul(u);
      for (let j = 0; j < X.n; j++) {
        const ang = (j / X.n) * Math.PI * 2, p = landing(u.x, u.y, u.x + Math.cos(ang) * 70, u.y + Math.sin(ang) * 70 * K);
        const s = this.spawnMinion(u.team, null, p[0], p[1], false);
        Object.assign(s, { temp: X.dur, owner: u, summon: true, maxHp: 260 + 20 * u.lv, hp: 260 + 20 * u.lv, atk: X.dmg * sp, asp: 90, armor: 4, ms: 430, r: 22, name: '虎豹騎', hubao: { first: true }, beast: 'hubao' }); // beast：只影響畫面
        this.fx({ k: 'puff', x: p[0], y: p[1] });
      }
      this.addBuff(u, { id: 'hubao', t: X.dur, ms: 0.1 });
    },
  },
  // 被動與專屬效果：hook(時間點, 順位, 處理)（時間點一覽見 core/hooks.ts）
  hooks: [
    hook('step.hero', 32, function (u, dt) {
      // 回馬箭：射程加成到期
      if (u.parthianT && u.parthianT <= this.t) { u.parthianT = 0; this.setRangeBonus(u, 'parthian', 0); }
      // 精騎疾馳：累積移動距離
      if (sigOf(u, 'gallop')) {
        if (u.gallopPos) u.gallopRun = (u.gallopRun || 0) + Math.hypot(u.x - u.gallopPos[0], (u.y - u.gallopPos[1]) / K);
        u.gallopPos = [u.x, u.y];
      }
      // 長坂追擊：朝目標移動時加速
      const pu = u.pursue;
      if (pu) {
        if (pu.until <= this.t || !pu.t.alive) { u.pursue = null; return; }
        const mv = u.pursuePos ? [u.x - u.pursuePos[0], u.y - u.pursuePos[1]] : [0, 0], dl = Math.hypot(mv[0], mv[1]);
        if (dl > 0.5) { const to = SG.dirTo(u, pu.t.x, pu.t.y); if ((mv[0] * to[0] + mv[1] * to[1]) / dl > 0.5) this.addBuff(u, { id: 'pursue', t: 0.2, ms: pu.ms / 100 }); }
        u.pursuePos = [u.x, u.y];
      }
    }),
    // 記錄每名武將這一步的移動方向（判斷「逃跑」用）
    hook('step.after', 32, function (dt) {
      for (const h of this.units) if (h.kind === 'hero' && h.alive) { h.runDir = h.runPos ? [h.x - h.runPos[0], h.y - h.runPos[1]] : [0, 0]; h.runPos = [h.x, h.y]; }
    }),
    // 長坂追擊：目標背對曹純移動時加傷
    hook('damage.before', 36, function (c) {
      const pu = c.own.pursue, t = c.t;
      if (pu && pu.t === t && pu.until > this.t && t.runDir) {
        const L = Math.hypot(t.runDir[0], t.runDir[1]);
        if (L > 0.5) { const away = SG.dirTo(c.own, t.x, t.y); if ((t.runDir[0] * away[0] + t.runDir[1] * away[1]) / L > 0.3) c.a *= 1.15; }
      }
    }),
    // 精騎疾馳：跑滿 400 的下一次普攻
    hook('onHit', 26, function (u, t) {
      const g = sigOf(u, 'gallop');
      if (g && (u.gallopRun || 0) >= 400 && t.alive) {
        u.gallopRun = 0;
        this.damage(u, t, g.v('dmg'), { stagger: 0.3 });
        if (t.alive && !t.structure) this.applyCC(u, t, { knockback: 70 });
      }
      // 虎豹騎：第一次命中擊退
      if (u.hubao && u.hubao.first && t.alive && !t.structure) { u.hubao.first = false; this.applyCC(u.owner || u, t, { knockback: 60 }); }
    }),
    // 虎豹騎：衝向曹純攻擊的目標
    hook('attack', 70, function (u, a, next) {
      const t = a.target;
      if (t && t.alive && u.kind === 'hero' && u.spec.id === 'caochun') for (const s of this.units) if (s.alive && s.hubao && s.owner === u) { s.target = t; s.think = 1; }
      return next();
    }),
    hook('revive', 72, function (u) { u.pursue = null; if (u.parthianT) { u.parthianT = 0; this.setRangeBonus(u, 'parthian', 0); } }),
  ],
  // 電腦什麼時候放這招
  ai: {
    parthian: ({ hero, d, t, hpR }) => !!t && d < 700 && (hero ? d < 450 || hpR < 0.5 : false),
    pursue: ({ hero, d, t }) => hero && !!t && d < 850 && t.hp / t.maxHp < 0.6,
    hubao: ({ hero, d, foeHeroes }) => hero && d < 650 && foeHeroes(900) >= 1,
  },
});
